import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";

import {
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserData,
  GetUSerActivityReportData,
} from "../Services/APIService";

// ✅ UPDATED: New interface matching API response
interface UserSummary {
  userId: number;
  userName: string;
  mobile: string;
  email: string;
  tenantId: number;

  totalLeads: number;
  newEnquiries: number;
  reinquire: number;
  totalEnquiries: number;
  followupCreated: number;
  completedTasks: number;
  noteCount: number;
  lost: number;
  booked: number;

  leadIds: string;
  completedTaskIds: string;
  noteTaskIds: string;
  bookedTaskIds: string;
  lostLeadIds: string;
  newEnquiryLogIds: string;
  reinquireLogIds: string;
  followupCreatedIds: string;
}

interface FilterParams {
  tenantId: number;
  fromDate?: string;
  toDate?: string;
  userId?: number;
  sourceId?: number;
  subSourceId?: number;
  leadTypeId?: number;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface User {
  userId: number;
  userName: string;
  userTypeId: number;
  isActive: boolean;
  reportTo:number;
}

interface OptionType {
  value: number;
  label: string;
}

// ✅ UPDATED: Static status definitions based on new API structure
type StatusDefinition = {
  key: keyof UserSummary;
  label: string;
};

const ReportView: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSummaries, setUserSummaries] = useState<UserSummary[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredSubSources, setFilteredSubSources] = useState<SubMediaSource[]>([]);

  const [filterParams, setFilterParams] = useState<FilterParams>({
    tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
    fromDate: "",
    toDate: "",
    userId: undefined,
    sourceId: undefined,
    subSourceId: undefined,
    leadTypeId: undefined,
  });

  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  // ✅ UPDATED: Static status definitions matching new API fields
  const statusDefinitions: StatusDefinition[] = [
    { key: "totalLeads", label: "Total Leads" },
    { key: "newEnquiries", label: "New Enquiries" },
    { key: "reinquire", label: "Reinquire" },
    { key: "totalEnquiries", label: "Total Enquiries" },
    { key: "followupCreated", label: "Followup Created" },
    { key: "completedTasks", label: "Completed Tasks" },
    { key: "noteCount", label: "Note Count" },
    { key: "lost", label: "Lost" },
    { key: "booked", label: "Booked" },
  ];

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");

        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getMenus();
  }, [location.state]);

  const fetchDropdownData = async () => {
    try {
      const sourceResponse = await fetchMeadiaSourceData();
      setMediaSources(sourceResponse.filter((s: MediaSource) => s.isActive));

      const subSourceResponse = await fetchAllSubMediaSource();
      setSubMediaSources(
        subSourceResponse.filter((s: SubMediaSource) => s.isActive)
      );

      const userResponse = await fetchUserData();
      setUsers(
        userResponse.filter(
          (u: User) => (u.userTypeId === 2 || u.userTypeId === 4) && u.isActive
        )
      );
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load filter options");
    }
  };

  // ✅ UPDATED: Fetch user activity data
 const fetchUserDetails = async () => {
  try {
    setLoading(true);
    const response: any = await GetUSerActivityReportData();

    const summaries: UserSummary[] = response.userActivity || [];
    setUserSummaries(summaries);
  } catch (error) {
    console.error("Error fetching user details:", error);
  } finally {
    setLoading(false);
  }
};
  const formatDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    let fromDate: Date;
    let toDate: Date;

    switch (period) {
      case "today": {
        fromDate = new Date(year, month, today.getDate());
        toDate = new Date(year, month, today.getDate());
        break;
      }
      case "yesterday": {
        const y = new Date(year, month, today.getDate() - 1);
        fromDate = y;
        toDate = y;
        break;
      }
      case "thisMonth": {
        fromDate = new Date(year, month, 1);
        toDate = today;
        break;
      }
      case "lastMonth": {
        fromDate = new Date(year, month - 1, 1);
        toDate = new Date(year, month, 0);
        break;
      }
      case "last3Months": {
        fromDate = new Date(year, month - 3, 1);
        toDate = new Date(year, month, 0);
        break;
      }
      case "last6Months": {
        fromDate = new Date(year, month - 6, 1);
        toDate = new Date(year, month, 0);
        break;
      }
      default: {
        setFilterParams((prev) => ({
          ...prev,
          fromDate: "",
          toDate: "",
        }));
        return;
      }
    }

    setFilterParams((prev) => ({
      ...prev,
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    }));
  };

  const handleLeadTypeChange = (leadTypeId: number) => {
    setFilterParams({
      ...filterParams,
      leadTypeId: leadTypeId || undefined,
    });
  };

 const applyFilter = async () => {
  try {
    setLoading(true);

    const data = await GetUSerActivityReportData(filterParams);

    setUserSummaries(data.userActivity || []);

  } catch (error) {
    toast.error("Filter failed");
  } finally {
    setLoading(false);
  }
};

  const clearFilter = () => {
    setFilterParams({
      tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
      fromDate: "",
      toDate: "",
      userId: undefined,
      sourceId: undefined,
      subSourceId: undefined,
      leadTypeId: undefined,
    });
    setSelectedPeriod("");
    setFilteredSubSources([]);
    fetchUserDetails();
  };

  // ✅ UPDATED: Calculate total for each user
  const calculateTotal = (user: UserSummary): number => {
    return (
      user.totalLeads +
      user.newEnquiries +
      user.reinquire +
      user.totalEnquiries +
      user.followupCreated +
      user.completedTasks +
      user.noteCount +
      user.lost +
      user.booked
    );
  };

  const filteredUsers = userSummaries.filter((user) => {
    const text = searchText.toLowerCase().trim();
    if (!text) return true;
    return user.userName.toLowerCase().includes(text);
  });

  // ✅ UPDATED: Export functions
  const exportToExcel = () => {
    try {
      const headers = [
        "User Name",
        ...statusDefinitions.map((s) => s.label),
        "Total",
      ];

      const rows = filteredUsers.map((user) => [
        user.userName,
        ...statusDefinitions.map((s) => user[s.key] || 0),
        calculateTotal(user),
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `user-activity-report-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportToPDF = () => {
    try {
      const statusHeaderCells = statusDefinitions
        .map((s) => `<th>${s.label}</th>`)
        .join("");

      const bodyRows = filteredUsers
        .map(
          (user) => `
            <tr>
              <td>${user.userName}</td>
              ${statusDefinitions
              .map((s) => `<td class="status-cell">${user[s.key] || 0}</td>`)
              .join("")}
              <td class="status-cell"><strong>${calculateTotal(user)}</strong></td>
            </tr>
          `
        )
        .join("");

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>User Activity Report</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #333;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #a78590;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .status-cell {
              text-align: center;
              font-weight: bold;
            }
            .meta-info {
              margin-bottom: 15px;
              font-size: 12px;
              color: #666;
            }
             
          </style>
        </head>
        <body>
          <h1>User Activity Report</h1>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            ${filterParams.fromDate
          ? `<strong>From Date:</strong> ${filterParams.fromDate}<br/>`
          : ""
        }
            ${filterParams.toDate
          ? `<strong>To Date:</strong> ${filterParams.toDate}<br/>`
          : ""
        }
            <strong>Total Records:</strong> ${filteredUsers.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>User Name</th>
                ${statusHeaderCells}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${bodyRows}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  useEffect(() => {
    fetchUserDetails();
    fetchDropdownData();
  }, []);

  const handleSourceOnChange = (sourceId: number | undefined) => {
    setFilterParams({
      ...filterParams,
      sourceId,
      subSourceId: undefined,
    });
  };

  useEffect(() => {
    if (filterParams.sourceId && subMediaSources.length > 0) {
      const filtered = subMediaSources.filter(
        (src) => src.mediaSourceID === filterParams.sourceId
      );
      setFilteredSubSources(filtered);
      if (!filtered.some((s) => s.id === filterParams.subSourceId)) {
        setFilterParams((prev) => ({
          ...prev,
          subSourceId: undefined,
        }));
      }
    } else {
      setFilteredSubSources([]);
      setFilterParams((prev) => ({
        ...prev,
        subSourceId: undefined,
      }));
    }
  }, [filterParams.sourceId, subMediaSources]);

  const getSelectedOption = (options: OptionType[], value?: number) => {
    return options.find((option) => option.value === value) || null;
  };

  const sourceOptions: OptionType[] = mediaSources.map((s) => ({
    value: s.sourceId,
    label: s.sourceName,
  }));

  const subSourceOptions: OptionType[] = filteredSubSources.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const userTypeId = Number(localStorage.getItem("userTypeId"));
const loggedUserId = Number(localStorage.getItem("UserId"));
const isLoginAsManager = localStorage.getItem("isLoginAsManager") === "true";

let filteredUserList = users;

// Admin → show all users
if (userTypeId === 1) {
  filteredUserList = users;
}

// Manager
else if (userTypeId === 2) {
  if (isLoginAsManager) {
    // show manager + team
    filteredUserList = users.filter(
      (u) => u.userId === loggedUserId || u.reportTo === loggedUserId
    );
  } else {
    // manager behaving like normal user
    filteredUserList = users.filter((u) => u.userId === loggedUserId);
  }
}

// Normal User
else {
  filteredUserList = users.filter((u) => u.userId === loggedUserId);
}

const userOptions: OptionType[] = filteredUserList.map((u) => ({
  value: u.userId,
  label: u.userName,
}));
  // ✅ CLICK HANDLER – OPEN LEAD DETAILS PAGE
const handleStatusClick = (
  user: UserSummary,
  statusKey: keyof UserSummary
) => {
  let ids = "";

  switch (statusKey) {
    case "completedTasks":
      ids = user.completedTaskIds;
      break;
    case "noteCount":
      ids = user.noteTaskIds;
      break;
    case "newEnquiries":
      ids = user.newEnquiryLogIds;
      break;
    case "reinquire":
      ids = user.reinquireLogIds;
      break;
    case "lost":
      ids = user.lostLeadIds;
      break;
    case "booked":
      ids = user.bookedTaskIds;
      break;
    case "followupCreated":
      ids = user.followupCreatedIds;
      break;
    default:
      ids = user.leadIds;
      break;
  }

  if (!ids) {
    toast.error("No records found");
    return;
  }

  const params = new URLSearchParams({
    leadIds: ids,
    userName: user.userName,
    statusName: String(statusKey),
  });

  // ✅ HashRouter compatible
  const url = `/#/user-activity-details?${params.toString()}`;

  window.open(url, "_blank");
};

  // ✅ UPDATED: Dynamic columns based on new structure
  const columns = [
    {
      name: "User Name",
      selector: (row: UserSummary) => row.userName,
      sortable: true,
      width: "220px",
      cell: (row: UserSummary) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.userName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.userName}
          </span>
        </div>
      ),
    },
    ...statusDefinitions.map((status, index) => ({
      name: status.label,
      cell: (row: UserSummary) => (
        <span
          className={`status-count-badge status-${index + 1}`}
          style={{ cursor: "pointer" }}
          onClick={() => handleStatusClick(row, status.key)} // ✅ FIX
          title="Click to view details"
        >
          {row[status.key] || 0}
        </span>
      ),
      width: "140px",
    })),


  ];

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">
                        User Activity Report
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Search by Name"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                          <i
                            className={`bi bi-${isFilterExpanded ? "chevron-up" : "funnel"
                              }`}
                          ></i>{" "}
                          {isFilterExpanded ? "Hide Filters" : "Show Filters"}
                        </button>
                      </div>
                    </div>

                    {/* Filter Section */}
                    {isFilterExpanded && (
                      <div
                        className="card-body"
                        style={{
                          borderBottom: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Period</label>
                            <select
                              className="form-select form-control-lg"
                              value={selectedPeriod}
                              onChange={(e) =>
                                handlePeriodChange(e.target.value)
                              }
                            >
                              <option value="">Select Period</option>
                              <option value="today">Today</option>
                              <option value="yesterday">Yesterday</option>
                              <option value="thisMonth">This Month</option>
                              <option value="lastMonth">
                                Last Month (exclude this month)
                              </option>
                              <option value="last3Months">
                                Last 3 Months (exclude this month)
                              </option>
                              <option value="last6Months">
                                Last 6 Months (exclude this month)
                              </option>
                            </select>
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.fromDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  fromDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.toDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  toDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">Lead Type</label>
                            <select
                              className="form-select form-control-lg"
                              value={filterParams.leadTypeId || ""}
                              onChange={(e) =>
                                handleLeadTypeChange(
                                  parseInt(e.target.value || "0")
                                )
                              }
                            >
                              <option value="">Select Lead Type</option>
                              <option value="1">New Inquiry</option>
                              <option value="6">Reinquiry</option>
                            </select>
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">User</label>
                            <Select
                              options={userOptions}
                              value={getSelectedOption(
                                userOptions,
                                filterParams.userId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  userId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select User"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Source</label>
                            <Select
                              options={sourceOptions}
                              value={getSelectedOption(
                                sourceOptions,
                                filterParams.sourceId
                              )}
                              onChange={(option) =>
                                handleSourceOnChange(option?.value)
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select Source"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Sub Source</label>
                            <Select
                              options={subSourceOptions}
                              value={getSelectedOption(
                                subSourceOptions,
                                filterParams.subSourceId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  subSourceId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder={
                                filterParams.sourceId
                                  ? "Select Sub Source"
                                  : "Select source first"
                              }
                              isSearchable
                              isClearable
                              isDisabled={!filterParams.sourceId}
                            />
                          </div>

                          <div
                            className="col-12"
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              className="btn btn-secondary"
                              onClick={clearFilter}
                            >
                              Clear
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={applyFilter}
                            >
                              Apply Filter
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredUsers}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No user activity data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Report Data"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        
        .status-count-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 13px;
          display: inline-block;
          transition: all 0.2s;
        }
        
        .status-count-badge:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .status-1 { /* Total Leads */
          background: #e3f2fd;
          color: #1976d2;
        }
        
        .status-2 { /* New Enquiries */
          background: #f3e5f5;
          color: #7b1fa2;
        }
        
        .status-3 { /* Reinquire */
          background: #ffebee;
          color: #c62828;
        }
        
        .status-4 { /* Total Enquiries */
          background: #fff3e0;
          color: #e65100;
        }
        
        .status-5 { /* Followup Created */
          background: #e0f2f1;
          color: #00695c;
        }
        
        .status-6 { /* Completed Tasks */
          background: #fce4ec;
          color: #ad1457;
        }
        
        .status-7 { /* Note Count */
          background: #e8f5e9;
          color: #2e7d32;
        }
        
        .status-8 { /* Lost */
          background: #ffebee;
          color: #b71c1c;
        }
        
        .status-9 { /* Booked */
          background: #e8f5e9;
          color: #2e7d32;
        }
        
        .total-badge {
          background: #e8f5e9;
          color: #2e597dff;
        }

        .css-1nmdiq5-menu {
          position: relative;
          z-index: 10000;
        }
      `}</style>
    </div>
  );
};

export default ReportView;